import { AlertTriangle, Ban } from "lucide-react";
import type { BuyerInterviewFlagDto, BuyerInterviewOutcomeDto } from "@/api/buyers";
import { ResponsiveGrid, StatusBadge } from "@/design-system/components/ui";
import { buyerLabel } from "../buyerFilters";
import { verdictTone } from "../buyerListBits";
import { DIMENSION_ORDER, dimensionLabel } from "./interviewBits";

/**
 * What the interview computed — revealed only after completion.
 *
 * <p>🔴 <b>Every dimension shows the RULE THAT PRODUCED IT.</b> A verdict with
 * its reasoning hidden is one nobody can argue with, and this replaced a
 * surface where five dropdowns produced five verdicts with no stated basis at
 * all. The rule text comes from the server with the verdict, so the two cannot
 * drift.</p>
 *
 * <p>SIX tiles. Financing is gone — that picture lives in the prequalification
 * system, and no interview produces one.</p>
 */
export function InterviewOutcomePanel({
  outcome,
  headline,
}: {
  outcome: BuyerInterviewOutcomeDto;
  /** Rendered above the tiles — the panel names the interview, the version
   *  drawer names the version. */
  headline?: React.ReactNode;
}) {
  const byDimension = new Map(outcome.dimensions.map((d) => [d.dimension, d]));
  // Registry order rather than payload order, so the tiles read the same way
  // every time and the same way the interview walked them.
  const ordered = DIMENSION_ORDER.map((key) => byDimension.get(key)).filter(
    (d): d is NonNullable<typeof d> => d != null,
  );

  return (
    <div className="grid gap-space-3" data-testid="interview-outcome">
      {headline}

      <div className="flex flex-wrap items-center gap-space-3">
        <StatusBadge tone={verdictTone(outcome.effectiveVerdict)}>
          {buyerLabel(outcome.effectiveVerdict)}
        </StatusBadge>
        {outcome.overriddenVerdict != null && (
          <span className="text-caption text-gray-mid" data-testid="interview-override-note">
            Overridden from {buyerLabel(outcome.computedVerdict)}
            {outcome.overrideReason ? ` — ${outcome.overrideReason}` : ""}
          </span>
        )}
      </div>

      <FlagList
        flags={outcome.knockouts}
        title="Knockouts"
        icon={Ban}
        tone="text-status-error"
        testId="interview-knockouts"
      />
      <FlagList
        flags={outcome.coherenceFlags}
        title="Things that do not add up"
        icon={AlertTriangle}
        tone="text-status-warning"
        testId="interview-coherence"
      />

      <ResponsiveGrid cols={3} minColWidth="16rem" gap="md">
        {ordered.map((d) => (
          <div
            key={d.dimension}
            className="min-w-0 rounded-md border border-border-light p-space-3"
            data-testid={`interview-dimension-${d.dimension}`}
          >
            <div className="flex items-start justify-between gap-space-2">
              <span className="text-caption uppercase tracking-wide text-gray-mid">
                {dimensionLabel(d.dimension)}
              </span>
              <StatusBadge tone={verdictTone(d.verdict)}>
                {buyerLabel(d.verdict)}
              </StatusBadge>
            </div>
            <p className="text-caption text-gray-mid m-0 mt-space-2">{d.ruleText}</p>
          </div>
        ))}
      </ResponsiveGrid>
    </div>
  );
}

function FlagList({
  flags,
  title,
  icon: Icon,
  tone,
  testId,
}: {
  flags: BuyerInterviewFlagDto[];
  title: string;
  icon: typeof Ban;
  tone: string;
  testId: string;
}) {
  if (flags.length === 0) return null;
  return (
    <div data-testid={testId}>
      <h4 className="text-caption uppercase tracking-wide text-gray-mid m-0 mb-space-1">
        {title}
      </h4>
      <ul className="m-0 pl-0 list-none grid gap-space-1">
        {flags.map((f) => (
          <li key={f.code} className="flex items-start gap-space-2 text-small text-onyx">
            <Icon size={13} className={`${tone} mt-[3px] shrink-0`} aria-hidden />
            <span>{f.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
